import { formatWithSymbol } from './format-with-symbol.ts';
import { EMPTY_DISPLAY_VALUE } from '../../constants/index.tsx';

type PriceValue = number | string | null | undefined;

type RenderPriceOptions = {
  currencySymbol?: string | null;
  emptyValue?: string;
  thousandsSeparator?: string;
};

const DEFAULT_CURRENCY_SYMBOL = '$';

const parsePriceValue = (value: number | string): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const rawValue = value.trim().replace(/\s/g, '');

  if (!rawValue) {
    return null;
  }

  const sign = rawValue.startsWith('-') ? '-' : '';
  const valueWithoutSign = rawValue.replace(/-/g, '').replace(/[^\d.,]/g, '');

  if (!/\d/.test(valueWithoutSign)) {
    return null;
  }

  const lastDotIndex = valueWithoutSign.lastIndexOf('.');
  const lastCommaIndex = valueWithoutSign.lastIndexOf(',');
  const decimalSeparatorIndex = Math.max(lastDotIndex, lastCommaIndex);
  const decimalPartLength = valueWithoutSign.length - decimalSeparatorIndex - 1;
  const hasDecimalPart = decimalSeparatorIndex >= 0 && decimalPartLength > 0 && decimalPartLength <= 2;

  const normalizedValue = hasDecimalPart
    ? `${sign}${valueWithoutSign.slice(0, decimalSeparatorIndex).replace(/[.,]/g, '')}.${valueWithoutSign.slice(decimalSeparatorIndex + 1).replace(/[.,]/g, '')}`
    : `${sign}${valueWithoutSign.replace(/[.,]/g, '')}`;

  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const renderPrice = (value: PriceValue, options?: RenderPriceOptions): string => {
  if (value === null || value === undefined) {
    return options?.emptyValue ?? EMPTY_DISPLAY_VALUE;
  }

  const parsedValue = parsePriceValue(value);

  if (parsedValue === null) {
    return options?.emptyValue ?? EMPTY_DISPLAY_VALUE;
  }

  const roundedValue = Math.floor(parsedValue);
  const sign = roundedValue < 0 ? '-' : '';
  const formattedValue = formatWithSymbol(Math.abs(roundedValue), options?.thousandsSeparator);
  const currencySymbol = options?.currencySymbol ?? DEFAULT_CURRENCY_SYMBOL;

  return `${sign}${currencySymbol}${formattedValue}`;
};
